/**
 * API utility functions for SAMA Member Portal
 * Includes CSRF protection for state-changing operations
 */

// Use REACT_APP_BACKEND_URL when set (split-host AWS deploy), otherwise fall back
// to relative '/api' so dev (CRA proxy) and same-origin deployments keep working.
const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// CSRF Token Header name (must match backend)
const CSRF_HEADER_NAME = 'X-CSRF-Token';

// Store last error for debugging
let lastApiError = null;

// CSRF token from last login response
let csrfToken = null;

// Initialize CSRF token from localStorage on load
try {
  const storedToken = localStorage.getItem('csrf_token');
  if (storedToken) {
    csrfToken = storedToken;
  }
} catch (e) {
  console.warn('Could not read CSRF token from localStorage:', e);
}

/**
 * Set CSRF token (called after login)
 */
export function setCsrfToken(token) {
  csrfToken = token;
  // Store in localStorage as backup
  if (token) {
    try {
      localStorage.setItem('csrf_token', token);
    } catch (e) {
      console.warn('Could not store CSRF token in localStorage:', e);
    }
  }
  // Also try to get from cookie as fallback
  if (!token) {
    const cookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrf_token='));
    if (cookie) {
      csrfToken = cookie.split('=')[1];
    }
  }
}

/**
 * Get CSRF token from cookie
 */
function getCsrfTokenFromCookie() {
  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('csrf_token='));
  if (cookie) {
    // Use indexOf to handle tokens that may contain '=' characters
    const eqIndex = cookie.indexOf('=');
    if (eqIndex !== -1) {
      return cookie.substring(eqIndex + 1);
    }
  }
  return null;
}

/**
 * Get CSRF token from localStorage (backup)
 */
function getCsrfTokenFromStorage() {
  try {
    return localStorage.getItem('csrf_token');
  } catch (e) {
    return null;
  }
}

/**
 * Get the current CSRF token
 */
export function getCsrfToken() {
  // Try stored token first, then cookie, then localStorage
  const token = csrfToken || getCsrfTokenFromCookie() || getCsrfTokenFromStorage();
  if (!token) {
    console.warn('[API] No CSRF token found in memory, cookie, or localStorage');
  }
  return token;
}

/**
 * Get the last API error for debugging
 */
export function getLastApiError() {
  return lastApiError;
}

/**
 * Copy debug info to clipboard
 */
export function copyDebugInfo() {
  if (!lastApiError) return false;
  
  const debugInfo = JSON.stringify({
    timestamp: lastApiError.timestamp,
    url: lastApiError.url,
    method: lastApiError.method,
    status: lastApiError.status,
    statusText: lastApiError.statusText,
    responseBody: lastApiError.responseBody?.substring(0, 2000),
    requestPayload: lastApiError.requestPayload ? '(redacted)' : null,
  }, null, 2);
  
  navigator.clipboard.writeText(debugInfo);
  return true;
}

/**
 * Refresh CSRF token from server
 */
async function refreshCsrfToken() {
  try {
    const response = await fetch(`${API}/auth/refresh-csrf`, {
      method: 'POST',
      credentials: 'include',
    });
    if (response.ok) {
      const data = await response.json();
      csrfToken = data.csrf_token;
      // Also store in localStorage
      if (data.csrf_token) {
        try {
          localStorage.setItem('csrf_token', data.csrf_token);
        } catch (e) {
          console.warn('Could not store refreshed CSRF token:', e);
        }
      }
      return true;
    }
  } catch (error) {
    console.error('Failed to refresh CSRF token:', error);
  }
  return false;
}

/**
 * Ensure a CSRF token is available — refresh from server if not.
 * Used by `secureFetch` so direct `fetch()` callers don't hit random
 * 403s when the in-memory token has been cleared (e.g. cross-tab).
 */
let _csrfRefreshInflight = null;
async function ensureCsrfToken() {
  const existing = getCsrfToken();
  if (existing) return existing;
  // Coalesce concurrent refresh calls so a burst of parallel direct-fetches
  // doesn't hammer /auth/refresh-csrf.
  if (!_csrfRefreshInflight) {
    _csrfRefreshInflight = refreshCsrfToken().finally(() => {
      _csrfRefreshInflight = null;
    });
  }
  await _csrfRefreshInflight;
  return getCsrfToken();
}

/**
 * `secureFetch` — drop-in wrapper around `fetch` that:
 *   1. Always attaches the latest CSRF token (refreshing it once if missing)
 *      for state-changing requests
 *   2. On 403 with CSRF body, refreshes the token and retries ONCE
 *   3. Preserves `credentials: 'include'` for session cookies
 *
 * Existing direct `fetch(${API_URL}/api/...)` callsites can migrate to this
 * helper to inherit the same auto-recovery behaviour `apiRequest` already has.
 * Components that still use raw `fetch` will continue to work — this is
 * additive, not a breaking change.
 */
export async function secureFetch(url, options = {}, isRetry = false) {
  const method = (options.method || 'GET').toUpperCase();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };

  if (['POST', 'PATCH', 'DELETE', 'PUT'].includes(method)) {
    const token = await ensureCsrfToken();
    if (token) headers[CSRF_HEADER_NAME] = token;
  }

  const config = { credentials: 'include', ...options, headers };
  const response = await fetch(url, config);

  if (response.status === 403 && !isRetry) {
    let body = '';
    try { body = await response.clone().text(); } catch { /* noop */ }
    if (body && body.includes('CSRF')) {
      csrfToken = null;
      const refreshed = await refreshCsrfToken();
      if (refreshed) return secureFetch(url, options, true);
    }
  }
  return response;
}

/**
 * Make an API request with credentials, CSRF protection, and error tracking
 */
async function apiRequest(endpoint, options = {}, skipAuthRedirect = false, isRetry = false) {
  const url = `${API}${endpoint}`;
  const method = options.method || 'GET';
  
  // Build headers
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  // Add CSRF token for state-changing methods
  if (['POST', 'PATCH', 'DELETE', 'PUT'].includes(method.toUpperCase())) {
    // Use `ensureCsrfToken` so we proactively refresh from the server when the
    // in-memory copy is missing (e.g. on first state-changing call after a
    // hard reload before /auth/me has finished). This kills the random
    // "CSRF validation token error" toasts users were seeing.
    const token = await ensureCsrfToken();
    if (token) {
      headers[CSRF_HEADER_NAME] = token;
    } else {
      console.warn('[API] No CSRF token available for', method, endpoint);
    }
  }
  
  const config = {
    credentials: 'include',
    headers,
    ...options,
  };

  let response;
  try {
    response = await fetch(url, config);
  } catch (networkError) {
    // Network error (DNS, CORS, etc)
    lastApiError = {
      timestamp: new Date().toISOString(),
      url: url,
      method: method,
      status: 0,
      statusText: 'Network Error',
      responseBody: networkError.message,
      requestPayload: options.body,
    };
    console.error('[API Network Error]', lastApiError);
    throw new Error(`Network error: ${networkError.message}`);
  }

  if (response.status === 401) {
    // Only redirect to login if not already on login page and not skipping redirect
    if (!skipAuthRedirect && !window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
    throw new Error('Unauthorized');
  }

  // Handle CSRF errors specifically - try to refresh and retry once
  if (response.status === 403) {
    let responseBody;
    try {
      responseBody = await response.text();
    } catch {
      responseBody = '';
    }

    // Profile-incomplete guard: backend rejects booking/waitlist APIs
    // for members who haven't finished onboarding. Redirect them to
    // the profile page so they can complete + retry.
    try {
      const parsed = JSON.parse(responseBody);
      const det = parsed?.detail;
      if (det && typeof det === 'object' && det.code === 'PROFILE_INCOMPLETE') {
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/account-preferences')) {
          const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/account-preferences?return=${returnTo}`;
        }
        const err = new Error(det.message || 'Please complete your profile before booking.');
        err.code = 'PROFILE_INCOMPLETE';
        throw err;
      }
    } catch (e) {
      if (e && e.code === 'PROFILE_INCOMPLETE') throw e;
      // fall through to CSRF handling
    }

    if (responseBody.includes('CSRF') && !isRetry) {
      console.warn('[API CSRF Error] Token expired. Attempting refresh...');
      // Clear stored token
      csrfToken = null;
      
      // Try to refresh the token and retry the request
      const refreshed = await refreshCsrfToken();
      if (refreshed) {
        console.log('[API] CSRF token refreshed, retrying request...');
        return apiRequest(endpoint, options, skipAuthRedirect, true);
      }
    }
    
    lastApiError = {
      timestamp: new Date().toISOString(),
      url: url,
      method: method,
      status: 403,
      statusText: 'Forbidden',
      responseBody: responseBody,
      requestPayload: options.body,
    };
    
    throw new Error('Session expired. Please refresh the page and try again.');
  }

  if (!response.ok) {
    // Capture error details for debugging
    let responseBody;
    try {
      responseBody = await response.text();
    } catch {
      responseBody = '(unable to read response body)';
    }
    
    lastApiError = {
      timestamp: new Date().toISOString(),
      url: url,
      method: method,
      status: response.status,
      statusText: response.statusText,
      responseBody: responseBody?.substring(0, 5000),
      requestPayload: options.body,
    };
    
    // Log for debugging
    console.error('[API Error]', lastApiError);
    
    // For 400 errors, show a more detailed message
    if (response.status === 400) {
      console.warn('400 Bad Request - Debug info captured. Use copyDebugInfo() to copy.');
    }
    
    // Try to parse error detail
    let errorDetail = 'Request failed';
    let errorPayload = null;
    try {
      const errorJson = JSON.parse(responseBody);
      // FastAPI's `detail` can be a string OR an object (our membership_not_started
      // guard sends a structured payload with code/message/...). Keep the object on
      // the thrown Error so callers can show a tailored UI instead of "[object Object]".
      const det = errorJson.detail ?? errorJson.message ?? errorDetail;
      if (det && typeof det === 'object') {
        errorPayload = det;
        errorDetail = det.message || det.detail || errorDetail;
      } else {
        errorDetail = det;
      }
    } catch {
      // Use raw response if not JSON
      if (responseBody && responseBody.length < 200) {
        errorDetail = responseBody;
      }
    }

    const err = new Error(errorDetail);
    err.status = response.status;
    if (errorPayload) err.payload = errorPayload;
    throw err;
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) return null;
  
  return JSON.parse(text);
}

// Auth APIs
export async function getMe() {
  return apiRequest('/auth/me', {}, true); // Skip auth redirect for this call
}

export async function logout() {
  return apiRequest('/auth/logout', { method: 'POST' });
}

// Studio/Schedule APIs
export async function getStudios() {
  return apiRequest('/studios');
}

export async function getClassTypes() {
  return apiRequest('/class-types');
}

export async function getSessions(studioId, date, days = 7) {
  let url = '/sessions';
  const params = new URLSearchParams();
  if (studioId) params.append('studio_id', studioId);
  if (date) params.append('date', date);
  if (days) params.append('days', days);
  if (params.toString()) url += `?${params.toString()}`;
  return apiRequest(url);
}

export async function getSession(sessionId) {
  return apiRequest(`/sessions/${sessionId}`);
}

// Booking APIs
export async function getMyBookings(status) {
  let url = '/my-bookings';
  if (status) url += `?status=${status}`;
  return apiRequest(url);
}

export async function createBooking(data) {
  // Support both old API (sessionId, idempotencyKey) and new API (object)
  let body;
  if (typeof data === 'string') {
    // Legacy call: createBooking(sessionId, idempotencyKey)
    body = {
      session_id: data,
      idempotency_key: arguments[1],
    };
  } else {
    // New call with object
    body = {
      session_id: data.session_id,
      idempotency_key: data.idempotency_key,
      utm_source: data.utm_source,
      utm_medium: data.utm_medium,
      utm_campaign: data.utm_campaign,
      utm_content: data.utm_content,
      referral_code: data.referral_code,
    };
  }
  
  return apiRequest('/book', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function cancelBooking(bookingId, { useLifeHappensToken = false } = {}) {
  const qs = useLifeHappensToken ? '?use_life_happens_token=true' : '';
  return apiRequest(`/book/${bookingId}/cancel${qs}`, {
    method: 'POST',
  });
}

export async function getLifeHappensBalance() {
  return apiRequest('/wallet/life-happens');
}

// Legacy function aliases for backward compatibility
export async function getServices(studioId, date) {
  // Map to class-types since we no longer have Wix services
  return getClassTypes();
}

export async function updateMe(data) {
  // No-op for now - profile updates not implemented
  return {};
}

export async function rescheduleBooking(bookingId, newSessionId) {
  // Not implemented yet
  throw new Error('Rescheduling not available');
}

// ============== WAITLIST APIs ==============

export async function joinWaitlist(sessionId) {
  return apiRequest(`/sessions/${sessionId}/waitlist/join`, {
    method: 'POST',
  });
}

export async function leaveWaitlist(sessionId) {
  return apiRequest(`/sessions/${sessionId}/waitlist/leave`, {
    method: 'POST',
  });
}

export async function getMyWaitlist() {
  return apiRequest('/my-waitlist');
}

export async function getSessionWaitlist(sessionId) {
  return apiRequest(`/sessions/${sessionId}/waitlist`);
}

// Admin Waitlist APIs
export async function adminGetSessionWaitlist(sessionId) {
  return apiRequest(`/admin/sessions/${sessionId}/waitlist`);
}

export async function adminPromoteWaitlist(waitlistId) {
  return apiRequest(`/admin/waitlist/${waitlistId}/promote`, {
    method: 'POST',
  });
}

export async function adminRemoveFromWaitlist(waitlistId, reason = 'Admin removed') {
  return apiRequest(`/admin/waitlist/${waitlistId}?reason=${encodeURIComponent(reason)}`, {
    method: 'DELETE',
  });
}

// Admin APIs
export async function getAdminStats(fromDate, toDate, studioId) {
  let url = '/admin/stats';
  const params = new URLSearchParams();
  if (fromDate) params.append('from_date', fromDate);
  if (toDate) params.append('to_date', toDate);
  if (studioId) params.append('studio_id', studioId);
  if (params.toString()) url += `?${params.toString()}`;
  return apiRequest(url);
}

export async function getAdminBookings(fromDate, toDate, studioId) {
  let url = '/admin/bookings';
  const params = new URLSearchParams();
  if (fromDate) params.append('from_date', fromDate);
  if (toDate) params.append('to_date', toDate);
  if (studioId) params.append('studio_id', studioId);
  if (params.toString()) url += `?${params.toString()}`;
  return apiRequest(url);
}

// Health check
export async function healthCheck() {
  return apiRequest('/health');
}

// Credit System APIs
export async function getCreditBalance() {
  return apiRequest('/wallet');
}

export async function getCreditHistory(limit = 20, opts = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (opts.date_from) params.set('date_from', opts.date_from);
  if (opts.date_to)   params.set('date_to',   opts.date_to);
  return apiRequest(`/credits/history?${params.toString()}`);
}

export async function getCreditPackages() {
  return apiRequest('/credits/packages');
}

export async function purchaseCredits(packageId, orderId = null) {
  return apiRequest('/credits/purchase', {
    method: 'POST',
    body: JSON.stringify({
      package_id: packageId,
      order_id: orderId,
    }),
  });
}

// Razorpay Payment APIs
export async function createRazorpayOrder(packageId, couponCode = null) {
  const body = { package_id: packageId };
  if (couponCode) body.coupon_code = couponCode;
  return apiRequest('/payments/create-order', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function verifyRazorpayPayment(orderId, paymentId, signature, packageId) {
  return apiRequest('/payments/verify', {
    method: 'POST',
    body: JSON.stringify({
      razorpay_order_id: orderId,
      razorpay_payment_id: paymentId,
      razorpay_signature: signature,
      package_id: packageId,
    }),
  });
}

export async function getClassCreditCosts() {
  return apiRequest('/credits/class-costs');
}

// Admin Credit APIs
export async function adminAdjustCredits(userId, creditsDelta, notes) {
  return apiRequest('/admin/credits/adjust', {
    method: 'POST',
    body: JSON.stringify({
      user_id: userId,
      credits_delta: creditsDelta,
      notes: notes,
    }),
  });
}

export async function adminSetClassCreditCost(serviceId, serviceName, creditCost) {
  return apiRequest('/admin/credits/class-cost', {
    method: 'POST',
    body: JSON.stringify({
      service_id: serviceId,
      service_name: serviceName,
      credit_cost: creditCost,
    }),
  });
}

export async function adminGetRecentBookings(limit = 20) {
  return apiRequest(`/admin/bookings/recent?limit=${limit}`);
}

// Wallet API
export async function getWallet() {
  return apiRequest('/wallet');
}

export async function getWalletFull() {
  return apiRequest('/wallet/full');
}

// ============== IDEMPOTENT BOOKING ==============

export async function createBookingIdempotent(serviceId, sessionId, slotStart, slotEnd, idempotencyKey, resourceId = null, locationId = null, studioId = null) {
  return apiRequest('/bookings/create', {
    method: 'POST',
    body: JSON.stringify({
      wix_service_id: serviceId,
      wix_session_id: sessionId,
      slot_start: slotStart,
      slot_end: slotEnd,
      idempotency_key: idempotencyKey,
      resource_id: resourceId,
      location_id: locationId,
      studio_id: studioId,
    }),
  });
}

// ============== COMPREHENSIVE ADMIN APIs ==============

// Services Management
export async function adminListServices() {
  return apiRequest('/admin/services');
}

export async function adminSyncServices() {
  return apiRequest('/admin/services/sync', { method: 'POST' });
}

export async function adminUpdateServiceCost(serviceId, creditCost) {
  return apiRequest(`/admin/services/${serviceId}/cost?credit_cost=${creditCost}`, { method: 'PUT' });
}

export async function adminBulkUpdateServiceCosts(serviceIds, creditCost) {
  return apiRequest(`/admin/services/bulk-update?credit_cost=${creditCost}`, {
    method: 'POST',
    body: JSON.stringify(serviceIds),
  });
}

// Packages Management
export async function adminListPackages() {
  return apiRequest('/admin/packages');
}

export async function adminCreatePackage(name, priceInr, creditsGranted, validityDays, description = '', badge = null, is_featured = false, features = [], display_to_members = true, allowed_class_type_ids = null) {
  const params = new URLSearchParams({
    name,
    price_inr: priceInr,
    credits_granted: creditsGranted,
    validity_days: validityDays,
    description: description || '',
    is_featured: is_featured,
    display_to_members: display_to_members,
  });
  if (badge) params.append('badge', badge);
  if (features && features.length > 0) {
    params.append('features', JSON.stringify(features.filter(f => f.trim())));
  }
  // Only send the restriction list when the admin picked "select formats"
  // AND actually checked at least one. Null / empty = universal (no
  // param sent, backend leaves the column as NULL).
  if (Array.isArray(allowed_class_type_ids) && allowed_class_type_ids.length > 0) {
    params.append('allowed_class_type_ids', JSON.stringify(allowed_class_type_ids));
  }
  return apiRequest(`/admin/packages?${params.toString()}`, {
    method: 'POST',
  });
}

export async function adminUpdatePackage(packageId, updates) {
  const params = new URLSearchParams();
  if (updates.name) params.append('name', updates.name);
  if (updates.price_inr) params.append('price_inr', updates.price_inr);
  if (updates.credits_granted) params.append('credits_granted', updates.credits_granted);
  if (updates.validity_days) params.append('validity_days', updates.validity_days);
  if (updates.description !== undefined) params.append('description', updates.description);
  if (updates.active !== undefined) params.append('active', updates.active);
  if (updates.badge !== undefined) params.append('badge', updates.badge || '');
  if (updates.is_featured !== undefined) params.append('is_featured', updates.is_featured);
  if (updates.display_to_members !== undefined) params.append('display_to_members', updates.display_to_members);
  if (updates.features !== undefined) {
    params.append('features', JSON.stringify(updates.features.filter(f => f.trim())));
  }
  // allowed_class_type_ids semantics:
  //   * undefined → don't touch the column
  //   * null / []  → clear restriction (universal)
  //   * [uuid,…]   → set restriction
  if (updates.allowed_class_type_ids !== undefined) {
    const val = Array.isArray(updates.allowed_class_type_ids) ? updates.allowed_class_type_ids : [];
    params.append('allowed_class_type_ids', JSON.stringify(val));
  }
  return apiRequest(`/admin/packages/${packageId}?${params.toString()}`, { method: 'PATCH' });
}

export async function getMyAllowedClassTypes() {
  // Returns { restricted, class_type_ids, package_names }.
  return apiRequest('/me/allowed-class-types');
}

export async function adminDeactivatePackage(packageId) {
  return apiRequest(`/admin/packages/${packageId}`, { method: 'DELETE' });
}

export async function adminPermanentDeletePackage(packageId) {
  return apiRequest(`/admin/packages/${packageId}/permanent`, { method: 'DELETE' });
}

// Members Management
export async function adminSearchMembers(query) {
  return apiRequest(`/admin/members/search?q=${encodeURIComponent(query)}`);
}

export async function adminGetMember(wixMemberId) {
  return apiRequest(`/admin/members/${wixMemberId}`);
}

export async function adminGrantCredits(wixMemberId, credits, notes = '', validityDays = 90) {
  return apiRequest(`/admin/members/${wixMemberId}/credits/grant?credits=${credits}&notes=${encodeURIComponent(notes)}&validity_days=${validityDays}`, {
    method: 'POST',
  });
}

export async function adminAdjustMemberCredits(wixMemberId, creditsDelta, notes = '') {
  return apiRequest(`/admin/members/${wixMemberId}/credits/adjust?credits_delta=${creditsDelta}&notes=${encodeURIComponent(notes)}`, {
    method: 'POST',
  });
}

// Bookings Management
export async function adminListBookings(limit = 50, status = null, fromDate = null, toDate = null) {
  const params = new URLSearchParams({ limit: limit.toString() });
  if (status) params.append('status', status);
  if (fromDate) params.append('from_date', fromDate);
  if (toDate) params.append('to_date', toDate);
  return apiRequest(`/admin/bookings?${params.toString()}`);
}

export async function adminVerifyBooking(wixBookingId) {
  return apiRequest(`/admin/bookings/${wixBookingId}/verify`);
}

export async function adminMarkNoShow(wixBookingId, notes = '') {
  return apiRequest(`/admin/bookings/${wixBookingId}/no-show?notes=${encodeURIComponent(notes)}`, {
    method: 'POST',
  });
}

// Waitlist Management
export async function adminListWaitlist(sessionId = null, serviceId = null, status = 'WAITING', limit = 50) {
  const params = new URLSearchParams({ limit: limit.toString() });
  if (sessionId) params.append('session_id', sessionId);
  if (serviceId) params.append('service_id', serviceId);
  if (status) params.append('status', status);
  return apiRequest(`/admin/waitlist?${params.toString()}`);
}

export async function adminDropWaitlistEntry(waitlistId, reason = 'Admin removed') {
  return apiRequest(`/admin/waitlist/${waitlistId}?reason=${encodeURIComponent(reason)}`, {
    method: 'DELETE',
  });
}

export async function adminPromoteWaitlistEntry(waitlistId) {
  return apiRequest(`/admin/waitlist/${waitlistId}/promote`, { method: 'POST' });
}

// Audit Logs
export async function adminGetAuditLogs(filters = {}) {
  const params = new URLSearchParams();
  if (filters.action) params.append('action', filters.action);
  if (filters.actor_type) params.append('actor_type', filters.actor_type);
  if (filters.wix_member_id) params.append('wix_member_id', filters.wix_member_id);
  if (filters.wix_booking_id) params.append('wix_booking_id', filters.wix_booking_id);
  if (filters.from_date) params.append('from_date', filters.from_date);
  if (filters.to_date) params.append('to_date', filters.to_date);
  if (filters.limit) params.append('limit', filters.limit.toString());
  return apiRequest(`/admin/audit-logs?${params.toString()}`);
}

// Poller/Sync
export async function adminRunSync() {
  return apiRequest('/admin/sync/run-now', { method: 'POST' });
}

export async function adminGetSyncLogs(limit = 20) {
  return apiRequest(`/admin/sync/logs?limit=${limit}`);
}

// ============== DEBUG APIs ==============

export async function debugHealth() {
  return apiRequest('/debug/health');
}

export async function debugGetLogs(lines = 200, token = null) {
  const params = new URLSearchParams({ lines: lines.toString() });
  if (token) params.append('token', token);
  return apiRequest(`/debug/logs?${params.toString()}`);
}

// ============== ADMIN: SCHEDULED JOBS ==============

export async function adminRunCreditExpiryJob(dryRun = false) {
  return apiRequest(`/admin/jobs/run-credit-expiry?dry_run=${dryRun}`, {
    method: 'POST',
  });
}

export async function adminGetExpiringCreditsStats() {
  return apiRequest('/admin/credits/expiring-stats');
}


// Generic API object for flexible endpoint calls
export const api = {
  get: (endpoint, options = {}) => apiRequest(endpoint, { method: 'GET', ...options }),
  post: (endpoint, body, options = {}) => apiRequest(endpoint, { 
    method: 'POST', 
    body: body ? JSON.stringify(body) : undefined,
    ...options 
  }),
  patch: (endpoint, body, options = {}) => apiRequest(endpoint, { 
    method: 'PATCH', 
    body: body ? JSON.stringify(body) : undefined,
    ...options 
  }),
  put: (endpoint, body, options = {}) => apiRequest(endpoint, { 
    method: 'PUT', 
    body: body ? JSON.stringify(body) : undefined,
    ...options 
  }),
  delete: (endpoint, options = {}) => apiRequest(endpoint, { method: 'DELETE', ...options }),
};
